/* Homepage preview: Bubbleworld plays right in the page.
   - The Play section is a game window. "Play now" (or any Play / Say hi button on
     the page, which scrolls there first) loads the game's script into this page
     (storefront pages can't be framed) and starts it in the window, next to a
     friend if the button names one.
   - Full screen moves the running game into a full-screen layer and back; Stop
     pauses it and shows the poster again. Scrolling the window out of view pauses
     it; scrolling back resumes.
   - When the game window comes into view (or a Play button is pointed at or
     touched), the game's script and styles are fetched early so the tap feels instant. */
(() => {
  const play = document.querySelector('.bbp-play');
  const arcade = play && play.querySelector('[data-bbp-arcade]');
  const stage = arcade && arcade.querySelector('[data-bbp-stage]');
  const dialog = document.querySelector('[data-bbp-game]');
  const hasGame = !!(play && arcade && stage && dialog);
  const js = dialog && dialog.dataset.gameJs;
  const css = dialog && dialog.dataset.gameCss;
  let booted = null;
  let running = false;
  let expanded = false;
  let inView = false;
  const event = (name) => window.dispatchEvent(new Event(`bubbleworld:${name}`));

  const addLink = (rel, href, as) => {
    const l = document.createElement('link');
    l.rel = rel;
    l.href = href;
    if (as) l.as = as;
    if (rel === 'modulepreload') l.crossOrigin = 'anonymous';
    document.head.appendChild(l);
    return l;
  };
  let warmed = false;
  const warm = () => {
    if (warmed || !hasGame) return;
    warmed = true;
    addLink('modulepreload', js);
    addLink('preload', css, 'style');
  };

  const boot = (meet) => {
    booted ??= (async () => {
      warm();
      addLink('stylesheet', css);
      await import(js);
      window.Bubbleworld.mount(stage, { meet, autostart: true });
    })();
    return booted;
  };
  // Pause whenever the game can't be seen; resume when it can.
  // Phones play full screen: a game window inside the page would take every
  // touch, so there'd be no way to scroll back out of it.
  const phone = () => matchMedia('(max-width: 800px), (pointer: coarse) and (max-width: 1024px)').matches;
  const sync = () => {
    if (!booted) return;
    const live = running && (expanded || inView) && !document.hidden;
    // While you play, the page's own animations hold still so the game gets the whole phone.
    document.documentElement.classList.toggle('bbp-playing', live);
    event(live ? 'resume' : 'pause');
  };

  const start = (meet, scroll) => {
    if (!hasGame) return;
    document.querySelector('[data-bbp-menu][open]')?.close();
    // Bring the game window itself into the middle of the screen.
    if (scroll) arcade.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    running = true;
    play.classList.add('is-on');
    if (phone()) expand();
    if (!booted) {
      play.classList.add('is-loading');
      boot(meet)
        .then(() => play.classList.remove('is-loading'))
        .catch(() => {
          play.classList.remove('is-loading', 'is-on');
          running = false;
        });
    } else sync();
  };
  const stop = () => {
    if (expanded) collapse(false);
    running = false;
    play.classList.remove('is-on');
    sync();
  };
  const expand = () => {
    if (!running || expanded) return;
    expanded = true;
    dialog.prepend(stage);
    dialog.showModal();
    history.pushState({ bbpFull: true }, '');
    sync();
  };
  const collapse = (fromHistory) => {
    if (!expanded) return;
    expanded = false;
    dialog.close();
    arcade.append(stage);
    if (!fromHistory && history.state?.bbpFull) history.back();
    // On a phone, leaving full screen is leaving the game: back to the page.
    if (phone()) {
      running = false;
      play.classList.remove('is-on');
      // After history.back() has restored its own scroll position.
      setTimeout(() => arcade.scrollIntoView({ block: 'center' }), 80);
    }
    sync();
  };

  if (hasGame) {
    if ('IntersectionObserver' in window) {
      // Fetch the game once its window is really on screen (it sits right under the
      // hero, so a margin here meant every visitor downloaded it). Pointing at or
      // touching a Play button warms it too, below. Data-saver visitors wait for that.
      if (!navigator.connection?.saveData) {
        new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && warm(), { threshold: 0.25 }).observe(arcade);
      }
      new IntersectionObserver((entries) => {
        inView = entries[entries.length - 1].isIntersecting;
        sync();
      }, { threshold: 0.3 }).observe(arcade);
    } else inView = true;
    document.addEventListener('visibilitychange', sync);
    const intent = (e) => e.target.closest?.('[data-bbp-play], [data-bbp-start]') && warm();
    document.addEventListener('pointerover', intent, { passive: true });
    document.addEventListener('touchstart', intent, { passive: true });
    document.addEventListener('click', (e) => {
      const link = e.target.closest?.('[data-bbp-play]');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      start(link.dataset.meet, true);
    });
    play.querySelector('[data-bbp-start]')?.addEventListener('click', () => start(undefined, false));
    play.querySelector('[data-bbp-expand]')?.addEventListener('click', expand);
    play.querySelector('[data-bbp-stop]')?.addEventListener('click', stop);
    dialog.querySelector('[data-bbp-close]')?.addEventListener('click', () => collapse(false));
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      collapse(false);
    });
    window.addEventListener('popstate', () => collapse(true));
  }

  // Menu: five tiles, one per part of the site, each with a friend peeking over.
  const menu = document.querySelector('[data-bbp-menu]');
  const opener = document.querySelector('[data-bbp-menu-open]');
  if (menu && opener) {
    opener.addEventListener('click', () => {
      menu.showModal();
      opener.setAttribute('aria-expanded', 'true');
    });
    menu.addEventListener('close', () => opener.setAttribute('aria-expanded', 'false'));
    menu.addEventListener('click', (e) => {
      // A tap on the backdrop, the close button, or any link (most go elsewhere
      // on this page) closes it.
      if (e.target === menu || e.target.closest('[data-bbp-menu-close], a')) menu.close();
    });
  }

  // Bottles: add straight to the bag from each flavor's ad, and stay on the page.
  const bag = document.querySelector('[data-bbp-bag]');
  let bagT = 0;
  document.querySelectorAll('[data-bbp-buy]').forEach((form) => {
    const qty = form.querySelector('input[name="quantity"]');
    form.querySelectorAll('[data-qty]').forEach((b) =>
      b.addEventListener('click', () => {
        qty.value = String(Math.min(24, Math.max(1, (Number(qty.value) || 1) + Number(b.dataset.qty))));
      }),
    );
    form.addEventListener('submit', async (e) => {
      const btn = form.querySelector('button[type="submit"]');
      if (!btn || !window.fetch) return; // no script help: a normal form post
      e.preventDefault();
      btn.classList.add('is-busy');
      try {
        const n = Math.max(1, Number(qty?.value) || 1);
        const item = { id: Number(form.elements.id.value), quantity: n };
        // Line-item properties (pickup day, keycap picks) ride along like a normal form post.
        const props = {};
        if (form.dataset.flavor) props.Flavor = form.dataset.flavor;
        for (const [k, v] of new FormData(form)) {
          const m = /^properties\[(.+)\]$/.exec(k);
          if (m && v) props[m[1]] = v;
        }
        if (Object.keys(props).length) item.properties = props;
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        if (!res.ok) throw new Error(String(res.status));
        const pick = form.elements.id.tagName === 'SELECT' ? ` (${form.elements.id.selectedOptions[0].text.split(' · ')[0]})` : '';
        bag.querySelector('b').textContent = `Added ${n} × ${form.dataset.flavor || form.dataset.name}${pick}`;
        // The header bag shows the new count.
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((c) => document.querySelectorAll('[data-bbp-count]').forEach((el) => {
            el.textContent = c.item_count;
            el.hidden = !c.item_count;
          }))
          .catch(() => {});
        bag.hidden = false;
        clearTimeout(bagT);
        bagT = setTimeout(() => (bag.hidden = true), 6000);
        const r = btn.getBoundingClientRect();
        window.bbpFizz?.confetti(r.left + r.width / 2, r.top, 8);
      } catch {
        form.submit();
      } finally {
        btn.classList.remove('is-busy');
      }
    });
  });

  // Find us: the market calendar. The next open day gets the big card and a
  // countdown; the days after it stack underneath, closures and notes included.
  // Everything is Philadelphia time.
  const trip = document.querySelector('[data-bbp-trip-scene]');
  const card = document.querySelector('[data-bbp-next]');
  if (trip && card) {
    let cal = null;
    try {
      cal = JSON.parse(document.querySelector('[data-bbp-schedule]')?.textContent || 'null');
    } catch {}
    const TZ = 'America/New_York';
    const isoDay = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
    const addDays = (iso, n) => {
      const d = new Date(`${iso}T12:00:00Z`);
      d.setUTCDate(d.getUTCDate() + n);
      return d.toISOString().slice(0, 10);
    };
    // The instant it is hh:mm on that day in Philadelphia.
    const at = (iso, hhmm) => {
      const [y, m, d] = iso.split('-').map(Number);
      const [hh, mm] = hhmm.split(':').map(Number);
      const guess = Date.UTC(y, m - 1, d, hh, mm);
      const shown = new Date(new Date(guess).toLocaleString('en-US', { timeZone: TZ }));
      const asUtc = new Date(new Date(guess).toLocaleString('en-US', { timeZone: 'UTC' }));
      return new Date(guess + (asUtc - shown));
    };
    const fmtTime = (hhmm) => {
      const [h, m] = hhmm.split(':').map(Number);
      return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`;
    };
    const dayName = (iso, style) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: style, month: style === 'long' ? 'long' : 'short', day: 'numeric' }).format(new Date(`${iso}T12:00:00Z`));
    const days = [];
    if (cal) {
      const now = new Date();
      for (let iso = [isoDay(now), cal.from].sort()[1]; iso <= cal.until; iso = addDays(iso, 1)) {
        const dow = new Date(`${iso}T12:00:00Z`).getUTCDay();
        const change = cal.changes?.[iso] || {};
        if (!cal.days.includes(dow) && !change.extra) continue;
        const open = at(iso, change.open || cal.open);
        const close = at(iso, change.close || cal.close);
        if (close <= now) continue;
        days.push({ iso, open, close, hours: `${fmtTime(change.open || cal.open)} to ${fmtTime(change.close || cal.close)}`, ...change });
      }
    }
    // An .ics file for one market day, so it lands in the visitor's calendar.
    const saveIcs = (d) => {
      const stamp = (t) => t.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Bubble Bears//Markets//EN', 'BEGIN:VEVENT', `UID:${d.iso}-sea-market@bubblebears.co`, `DTSTAMP:${stamp(new Date())}`, `DTSTART:${stamp(d.open)}`, `DTEND:${stamp(d.close)}`, `SUMMARY:Bubble Bears at ${cal.name}`, `LOCATION:${cal.address.replace(/,/g, '\\,')}`, `DESCRIPTION:${d.note ? `${d.note} ` : ''}Come say hi!`, 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
      a.download = `bubble-bears-${d.iso}.ics`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    };
    // Tap any market day for the details: when, where, notes, directions, calendar.
    const modal = document.querySelector('[data-event-modal]');
    const openDay = (d) => {
      if (!modal || !d) return;
      const m = (sel) => modal.querySelector(sel);
      m('[data-em-title]').textContent = cal.name;
      m('[data-em-when]').textContent = `${dayName(d.iso, 'long')} · ${d.hours}`;
      m('[data-em-where]').textContent = cal.address;
      m('[data-em-note]').textContent = d.note || '';
      m('[data-em-note]').hidden = !d.note;
      m('[data-em-go]').href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(cal.address)}`;
      m('[data-em-cal]').onclick = () => saveIcs(d);
      modal.showModal();
    };
    modal?.querySelector('[data-em-close]')?.addEventListener('click', () => modal.close());
    modal?.addEventListener('click', (e) => e.target === modal && modal.close());
    const next = days.find((d) => !d.closed);
    const q = (sel) => card.querySelector(sel);
    const go = q('[data-next-go]');
    if (cal) go.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(cal.address)}`;
    if (next) {
      card.querySelector('.bbp-next').addEventListener('click', (e) => !e.target.closest('a, button') && openDay(next));
      card.querySelector('.bbp-next').setAttribute('role', 'button');
      card.querySelector('.bbp-next').tabIndex = 0;
      card.querySelector('.bbp-next').addEventListener('keydown', (e) => e.key === 'Enter' && e.target === e.currentTarget && openDay(next));
    }
    if (!next) {
      // The season's over (or there's no calendar): point people at the list.
      q('[data-next-label]').textContent = cal ? 'That’s a wrap' : 'Next stop';
      if (cal) q('[data-next-when]').textContent = 'The market season is over. Get on the list and we’ll tell you where we pop up next.';
    } else {
      q('[data-next-title]').textContent = cal.name;
      q('[data-next-when]').textContent = `${dayName(next.iso, 'long')} · ${next.hours}`;
      if (next.note) {
        q('[data-next-note]').textContent = next.note;
        q('[data-next-note]').hidden = false;
      }
      const cd = q('[data-countdown]');
      cd.hidden = false;
      const tick = () => {
        const now = Date.now();
        if (now >= next.open && now < next.close) {
          q('[data-next-label]').textContent = 'We’re open right now';
          cd.hidden = true;
          return;
        }
        let left = Math.max(0, Math.floor((next.open - now) / 1000));
        const parts = { d: Math.floor(left / 86400), h: Math.floor((left % 86400) / 3600), m: Math.floor((left % 3600) / 60), s: left % 60 };
        for (const [k, v] of Object.entries(parts)) cd.querySelector(`[data-cd="${k}"]`).textContent = v;
        setTimeout(tick, 1000);
      };
      tick();
      const calBtn = q('[data-next-cal]');
      calBtn.hidden = false;
      calBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        saveIcs(next);
      });
    }
    // The stack: the open market days after the next one (closed days aren't shown).
    const list = q('[data-bbp-upcoming]');
    days.filter((d) => d !== next && !d.closed).slice(0, 5).forEach((d) => {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bbp-event';
      b.innerHTML = `<b></b><span></span>${d.note ? '<small></small>' : ''}`;
      b.querySelector('b').textContent = dayName(d.iso, 'short');
      b.querySelector('span').textContent = d.hours;
      if (d.note) b.querySelector('small').textContent = d.note;
      b.addEventListener('click', () => openDay(d));
      li.append(b);
      list.append(li);
    });
    list.hidden = !list.children.length;
    // Only drive while it's on screen.
    if ('IntersectionObserver' in window) new IntersectionObserver(([e]) => trip.classList.toggle('is-still', !e.isIntersecting)).observe(trip);
  }

  // The Bubble Feed: tap a post (or a friend dropping in) for a closer look.
  // The lanes pause, it opens here with sprinkles, and you can step through or
  // open it in Instagram; nothing whisks you off the site.
  const post = document.querySelector('[data-bbp-post]');
  const feed = document.querySelector('.bbp-feed');
  if (post && feed) {
    feed.querySelectorAll('.bbp-lane-copy [data-post]').forEach((b) => (b.tabIndex = -1));
    const cards = [...feed.querySelectorAll('.bbp-lane-track > [data-post]')];
    const img = post.querySelector('[data-post-img]');
    const cap = post.querySelector('[data-post-cap]');
    const go = post.querySelector('[data-post-go]');
    let at = 0;
    const show = (i) => {
      at = (i + cards.length) % cards.length;
      const c = cards[at].dataset;
      const friend = c.kind === 'friend';
      post.classList.toggle('is-friend', friend);
      post.style.setProperty('--post-bg', friend ? getComputedStyle(cards[at]).getPropertyValue('--bg') : '#fff');
      const acts = { coolbear: 'bbp-act-vibe', bubbles: 'bbp-act-hops', berry: 'bbp-act-sway', doba: 'bbp-act-jiggle', tedamame: 'bbp-act-squish' };
      post.style.setProperty('--act', friend ? acts[c.meet] : 'none');
      img.src = c.src;
      img.alt = c.cap;
      cap.textContent = c.cap;
      if (friend) {
        // Bubbleworld has its own page now; meet that friend there.
        const playUrl = document.querySelector('.bbp-play-pill')?.getAttribute('href') || '/pages/test';
        go.href = c.meet === 'coolbear' ? playUrl : `${playUrl}?meet=${c.meet}`;
        go.target = '';
        go.textContent = c.meet === 'coolbear' ? 'Play as Cool Bear' : `Say hi to ${c.name}`;
      } else {
        go.href = c.url;
        go.target = '_blank';
        go.textContent = c.url.includes('/p/') ? 'Open in Instagram' : 'See more on Instagram';
      }
    };
    feed.addEventListener('click', (e) => {
      const card = e.target.closest('[data-post]');
      if (!card) return;
      const i = cards.findIndex((c) => c.dataset.src === card.dataset.src && c.dataset.cap === card.dataset.cap);
      show(Math.max(0, i));
      feed.classList.add('is-paused');
      post.showModal();
    });
    post.addEventListener('close', () => feed.classList.remove('is-paused'));
    post.addEventListener('click', (e) => {
      if (e.target === post || e.target.closest('[data-post-close]')) post.close();
      const step = e.target.closest('[data-post-step]');
      if (step) show(at + Number(step.dataset.postStep));
    });
    post.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') show(at + 1);
      if (e.key === 'ArrowLeft') show(at - 1);
    });
  }

  // The Bubble List: the ticked interests become customer tags, so blasts can
  // go to just the people who want them. The welcome code copies on tap.
  const list = document.getElementById('bbp-list-form');
  if (list) {
    list.addEventListener('submit', () => {
      // Without the interest picks, everyone hears about everything (the default tags).
      if (!list.querySelector('.bbp-list-picks')) return;
      const picks = [...list.querySelectorAll('.bbp-list-picks input:checked')].map((i) => i.value);
      const tags = list.querySelector('[data-bbp-tags]');
      if (tags) tags.value = ['newsletter', ...picks].join(',');
    });
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-fizz-copy-code]');
    if (b) navigator.clipboard?.writeText(b.dataset.fizzCopyCode).then(() => (b.textContent = 'Copied!'));
  });

  // Section titles pop in as they come on screen.
  const reveals = document.querySelectorAll('[data-reveal]');
  if ((reveals.length || document.querySelector('.bb-footer')) && 'IntersectionObserver' in window) {
    document.querySelector('[data-bb-review]')?.classList.add('bbp-js');
    const rio = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          rio.unobserve(e.target);
        }),
      { rootMargin: '0px 0px -12% 0px' },
    );
    reveals.forEach((r) => rio.observe(r));
    const foot = document.querySelector('.bb-footer');
    if (foot) rio.observe(foot);
  }

  // Merch shelf: every thing on the pegboard does something.
  const shelf = document.querySelector('.bbp-shelf');
  if (shelf) {
    const art = document.querySelector('[data-bb-review]')?.dataset || {};
    const crew = ['coolbear', 'bubbles', 'berry', 'doba', 'tedamame'].map((k) => art[`art${k[0].toUpperCase()}${k.slice(1)}`]).filter(Boolean);
    const retrigger = (el, cls) => {
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    };
    // Keychains: all five charms on one ring; they jingle.
    shelf.querySelectorAll('[data-jingle]').forEach((ring) => {
      const go = () => {
        retrigger(ring, 'is-jingling');
        window.bbpFizz?.jingle();
      };
      ring.addEventListener('click', go);
      ring.closest('li').addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && retrigger(ring, 'is-jingling'));
    });
    // Clickers: a real keycap. Press it; it clicks, and a new friend pops in.
    shelf.querySelectorAll('[data-clicker]').forEach((cap) => {
      const img = cap.querySelector('img');
      let n = 1;
      cap.addEventListener('pointerdown', () => {
        cap.classList.add('is-down');
        window.bbpFizz?.click();
      });
      const up = () => {
        if (!cap.classList.contains('is-down')) return;
        cap.classList.remove('is-down');
        window.bbpFizz?.click(true);
        n = (n + 1) % crew.length;
        // The first picture has a srcset for its size; swaps use the plain file.
        if (crew[n]) {
          img.removeAttribute('srcset');
          img.src = crew[n];
        }
        retrigger(img, 'is-new');
      };
      cap.addEventListener('pointerup', up);
      cap.addEventListener('pointerleave', up);
      cap.addEventListener('keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        cap.classList.add('is-down');
        window.bbpFizz?.click();
        setTimeout(up, 90);
      });
    });
    // Bubble waffle: pop every bubble; when they're all popped, it puffs back up.
    shelf.querySelectorAll('[data-waffle]').forEach((w) => {
      const cells = [...w.querySelectorAll('.bbp-waffle-cell')];
      cells.forEach((c) =>
        c.addEventListener('click', () => {
          if (c.classList.contains('is-popped')) return;
          c.classList.add('is-popped');
          window.bbpFizz?.pop();
          if (cells.every((x) => x.classList.contains('is-popped'))) {
            setTimeout(() => cells.forEach((x, i) => setTimeout(() => x.classList.remove('is-popped'), i * 45)), 700);
            const r = w.getBoundingClientRect();
            window.bbpFizz?.confetti(r.left + r.width / 2, r.top, 10);
          }
        }),
      );
    });
    // Chains swing; clothes spin.
    shelf.querySelectorAll('[data-swing], [data-spin]').forEach((li) =>
      li.addEventListener('click', () => {
        retrigger(li, li.hasAttribute('data-spin') ? 'is-spinning' : 'is-swinging');
        if (li.hasAttribute('data-swing')) window.bbpFizz?.jingle();
      }),
    );
    // A gentle 3D tilt under the mouse.
    if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
      shelf.querySelectorAll('.bbp-shelf-item').forEach((li) => {
        li.addEventListener('pointermove', (e) => {
          const r = li.getBoundingClientRect();
          li.style.setProperty('--tx', (((e.clientX - r.left) / r.width) * 2 - 1).toFixed(2));
          li.style.setProperty('--ty', (((e.clientY - r.top) / r.height) * 2 - 1).toFixed(2));
        });
        li.addEventListener('pointerleave', () => {
          li.style.setProperty('--tx', 0);
          li.style.setProperty('--ty', 0);
        });
      });
    }
  }

  // The crew you've met waves you off at the bottom of the page. Everyone you
  // saw (and everyone you tapped, first) lines up along the footer's edge,
  // heads up, the rest of them tucked behind it. New friends join by adding a
  // data-art-<name> image to the page root.
  const siteRoot = document.querySelector('[data-bb-review]');
  const footer = document.querySelector('.bb-footer');
  if (siteRoot && footer) {
    const names = { coolbear: 'Cool Bear', bubbles: 'Bubbles', berry: 'Berry', doba: 'Doba', tedamame: 'Tedamame' };
    const art = Object.entries(siteRoot.dataset)
      .filter(([k]) => k.startsWith('art'))
      .map(([k, src]) => [k.slice(3).toLowerCase(), src]);
    let met = { seen: [], tapped: [] };
    try {
      met = { ...met, ...JSON.parse(localStorage.getItem('bbp-met') || '{}') };
    } catch {}
    const keep = () => {
      try {
        localStorage.setItem('bbp-met', JSON.stringify(met));
      } catch {}
    };
    const note = (id, list) => {
      if (!id || met[list].includes(id)) return;
      met[list].push(id);
      keep();
      draw();
    };
    const idFrom = (el) => el.dataset.friend || el.dataset.peek || el.dataset.meet || el.className.match(/bbp-(?:toy|pack)--(\w+)/)?.[1];
    const friends = document.querySelectorAll('.bbp-toy, .bbp-pack, [data-peek], .bbp-post-card--char');
    const seenIO = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && note(idFrom(e.target), 'seen')), { threshold: 0.5 });
    friends.forEach((f) => seenIO.observe(f));
    document.addEventListener('click', (e) => {
      const f = e.target.closest('.bbp-toy, .bbp-pack, [data-peek], .bbp-post-card--char, [data-meet]');
      if (f) note(idFrom(f), 'tapped');
    });
    const row = document.createElement('div');
    row.className = 'bbp-footer-crew';
    row.setAttribute('aria-hidden', 'true');
    footer.append(row);
    function draw() {
      const order = [...new Set([...met.tapped, ...met.seen, ...(met.seen.length ? [] : art.map(([id]) => id))])];
      const list = order.map((id) => art.find(([k]) => k === id)).filter(Boolean);
      row.style.setProperty('--n', list.length);
      row.innerHTML = list.map(([id, src], i) => `<span class="bbp-fc bbp-fc--${id}" style="--i:${i}"><img src="${src}" alt="" loading="lazy"><i>${names[id] || id}</i></span>`).join('');
    }
    draw();
  }

  // Hero: the toy line-up. Tap a toy and it hops and says hi (a little card with
  // its keychain and a way to meet it in the game); tap a soap bubble to pop it.
  // A mouse nudges the layers for depth. Everything holds still off screen.
  const box = document.querySelector('[data-bbp-toybox]');
  if (box) {
    const toys = [...box.querySelectorAll('.bbp-toy')];
    const idOf = (toy) => toy?.className.match(/bbp-toy--(\w+)/)[1];
    // A friend's world: the hero's background becomes theirs and they do their
    // thing (CSS). Hover or focus shows it; an open card keeps it.
    const world = (toy) => {
      if (toy) box.dataset.world = idOf(toy);
      else delete box.dataset.world;
      toys.forEach((t) => t.classList.toggle('is-featured', t === toy));
    };
    const picked = () => toys.find((t) => t.classList.contains('is-picked'));
    // A little something pops up over a friend's head when it's their turn.
    const EMOTES = { coolbear: 'star', bubbles: 'pearl', berry: 'heart', doba: 'coin', tedamame: 'leaf' };
    const emote = (toy) => {
      const id = idOf(toy);
      for (let i = 0; i < 3; i++) {
        const e = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        e.setAttribute('class', 'bbp-emote');
        e.style.setProperty('--k', i);
        e.innerHTML = `<use href="#bbp-i-${EMOTES[id] || 'star'}"/>`;
        toy.append(e);
        setTimeout(() => e.remove(), 1900);
      }
    };
    let leave = 0;
    const rest = () => {
      clearTimeout(leave);
      leave = setTimeout(() => world(picked()), 700);
    };
    const pick = (toy) => {
      toys.forEach((t) => {
        const on = t === toy && !t.classList.contains('is-picked');
        t.classList.toggle('is-picked', on);
        t.querySelector('.bbp-toy-hit').setAttribute('aria-expanded', String(on));
      });
      clearTimeout(leave);
      world(picked() || (toy && matchMedia('(hover: hover)').matches ? toy : null));
    };
    toys.forEach((toy) => {
      const hit = toy.querySelector('.bbp-toy-hit');
      const zone = toy.querySelector('.bbp-toy-zone');
      hit.addEventListener('click', () => {
        pick(toy);
        emote(toy);
      });
      // Hover intent: sweeping the mouse across the crew doesn't flick through every
      // world; a friend has to be pointed at for a moment first.
      let intent = 0;
      zone.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(intent);
        intent = setTimeout(() => {
          clearTimeout(leave);
          if (!toy.classList.contains('is-featured')) emote(toy);
          world(toy);
        }, 260);
      });
      zone.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(intent);
        rest();
      });
      hit.addEventListener('focus', () => {
        clearTimeout(leave);
        world(toy);
      });
      hit.addEventListener('blur', rest);
      // Any hand on the crew pauses the show for a while.
      ['pointerenter', 'focusin', 'click'].forEach((ev) => toy.addEventListener(ev, () => (touched = Date.now()), true));
    });
    // The show: each friend takes a turn, doing their move while the hero
    // crossfades into their world. Hands off for a few seconds and it carries on.
    let touched = 0;
    let turn = -1;
    const calm = matchMedia('(prefers-reduced-motion: reduce)');
    setInterval(() => {
      if (calm.matches || document.hidden || box.classList.contains('is-still') || picked() || Date.now() - touched < 6000) return;
      turn = (turn + 1) % toys.length;
      world(toys[turn]);
      emote(toys[turn]);
    }, 3600);
    // Cool Bear's skyline stands on the tabletop.
    const lineup = box.querySelector('.bbp-lineup');
    const horizon = () => {
      const b = box.getBoundingClientRect();
      const l = lineup.getBoundingClientRect();
      const u = l.height / (innerWidth <= 800 ? 1.67 : 1.5);
      box.style.setProperty('--horizon', `${Math.round(l.bottom - b.top - u * 0.1)}px`);
    };
    horizon();
    addEventListener('resize', horizon);
    document.addEventListener('click', (e) => {
      if (!e.target.closest?.('.bbp-toy')) toys.forEach((t) => t.classList.contains('is-picked') && pick(t));
    });
    box.addEventListener('keydown', (e) => {
      const open = toys.find((t) => t.classList.contains('is-picked'));
      if (e.key !== 'Escape' || !open) return;
      pick(open);
      open.querySelector('.bbp-toy-hit').focus();
    });

    if (matchMedia('(hover: hover) and (pointer: fine)').matches && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      let frame = 0;
      box.addEventListener('pointermove', (e) => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const r = box.getBoundingClientRect();
          box.style.setProperty('--px', (((e.clientX - r.left) / r.width) * 2 - 1).toFixed(3));
          box.style.setProperty('--py', (((e.clientY - r.top) / r.height) * 2 - 1).toFixed(3));
        });
      });
    }
    if ('IntersectionObserver' in window) new IntersectionObserver(([e]) => box.classList.toggle('is-still', !e.isIntersecting)).observe(box);
    // The site's nav draws a cream band for legibility over content; over the hero
    // the toys' own colour field is enough.
    const site = box.closest('[data-bb-review]');
    const band = () => site?.classList.toggle('bbp-over-hero', box.getBoundingClientRect().bottom > 90);
    addEventListener('scroll', band, { passive: true });
    addEventListener('resize', band);
    band();
  }
})();

// Pickup day: made-to-order drinks go into tomorrow's first batch (lead 1); the frozen
// weekend batch can be picked up today (lead 0). Dates are Philadelphia time.
(() => {
  const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
  const addDays = (iso, n) => {
    const d = new Date(`${iso}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };
  document.querySelectorAll('[data-bbp-pickup-day]').forEach((input) => {
    const min = addDays(today(), Number(input.dataset.lead || 1));
    input.min = min;
    input.max = addDays(min, 30);
    if (!input.value || input.value < min) input.value = min;
  });
})();

// Request page: ?type= picks the kind of request; the delivery area only asks when it's delivery.
(() => {
  const types = document.querySelector('[data-bbp-request-types]');
  if (!types) return;
  const want = new URLSearchParams(location.search).get('type');
  const pick = want && types.querySelector(`[data-type="${want}"]`);
  if (pick) pick.checked = true;
  const sync = () => {
    const t = types.querySelector('input:checked')?.dataset.type;
    document.querySelectorAll('[data-bbp-show]').forEach((el) => (el.hidden = el.dataset.bbpShow !== t));
  };
  types.addEventListener('change', sync);
  sync();
})();

// Shy friends: point at one and it ducks for a moment (once, then a short
// breather, so it can't flicker under a still mouse); click one and it's gone
// until you scroll it out of sight. data-bbp-shy="click" skips the ducking.
(() => {
  const shy = document.querySelectorAll('[data-bbp-shy]');
  if (!shy.length) return;
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((es) => es.forEach((e) => !e.isIntersecting && e.target.classList.remove('is-gone')))
    : null;
  shy.forEach((el) => {
    io?.observe(el);
    let calmUntil = 0;
    if (el.dataset.bbpShy !== 'click') {
      el.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || Date.now() < calmUntil || el.classList.contains('is-gone')) return;
        el.classList.add('is-ducking');
        calmUntil = Date.now() + 4000;
        setTimeout(() => el.classList.remove('is-ducking'), 2200);
      });
    }
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      el.classList.remove('is-ducking');
      el.classList.add('is-gone');
      window.bbpFizz?.pop?.();
    });
  });
})();
